(() => {
  "use strict";

  const TOKEN_SINGLE_PRODUCT = /\[\[product:([^\|\]]+?)(?:\|([^\]]+?))?\]\]/gi;
  const TOKEN_MULTI_PRODUCT = /\[\[products:([^\|\]]+?)(?:\|([^\]]+?))?\]\]/gi;
  const TOKEN_SINGLE_COLLECTION = /\[\[collection:([^\|\]]+?)(?:\|([^\]]+?))?\]\]/gi;
  const TOKEN_MULTI_COLLECTION = /\[\[collections:([^\|\]]+?)(?:\|([^\]]+?))?\]\]/gi;

  function normalizeHandle(input) {
    return String(input || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\-]/g, "");
  }

  function parseHandles(csv) {
    return String(csv || "")
      .split(",")
      .map((s) => normalizeHandle(s))
      .filter(Boolean);
  }

  async function fetchCardHtml(url) {
    const res = await fetch(url, { headers: { "Accept": "text/html" } });
    if (!res.ok) {
      throw new Error(`Card fetch failed: ${url}`);
    }
    return await res.text();
  }

  function createElementFromHtml(html) {
    const tpl = document.createElement("template");
    tpl.innerHTML = String(html || "").trim();
    return tpl.content.firstElementChild;
  }

  function buildError(message) {
    const el = document.createElement("div");
    el.className = "lfdl-embed-error";
    el.textContent = message;
    return el;
  }

  function replaceTokensInHtml(root) {
    const before = root.innerHTML;

    const after = before
      .replace(TOKEN_MULTI_PRODUCT, (match, handlesCsv, opt) => {
        const handles = parseHandles(handlesCsv);
        if (!handles.length) return match;

        const layout = String(opt || "grid2").trim().toLowerCase();
        const safeHandles = handles.join(",");

        return `<div class="lfdl-embed-products" data-product-handles="${safeHandles}" data-layout="${layout}"></div>`;
      })
      .replace(TOKEN_SINGLE_PRODUCT, (match, handleRaw, opt) => {
        const handle = normalizeHandle(handleRaw);
        if (!handle) return match;

        const align = String(opt || "").trim().toLowerCase();
        const alignAttr = align ? ` data-align="${align}"` : "";

        return `<div class="lfdl-embed-product" data-product-handle="${handle}"${alignAttr}></div>`;
      })
      .replace(TOKEN_MULTI_COLLECTION, (match, handlesCsv, opt) => {
        const handles = parseHandles(handlesCsv);
        if (!handles.length) return match;

        const layout = String(opt || "grid2").trim().toLowerCase();
        const safeHandles = handles.join(",");

        return `<div class="lfdl-embed-collections" data-collection-handles="${safeHandles}" data-layout="${layout}"></div>`;
      })
      .replace(TOKEN_SINGLE_COLLECTION, (match, handleRaw, opt) => {
        const handle = normalizeHandle(handleRaw);
        if (!handle) return match;

        const align = String(opt || "").trim().toLowerCase();
        const alignAttr = align ? ` data-align="${align}"` : "";

        return `<div class="lfdl-embed-collection" data-collection-handle="${handle}"${alignAttr}></div>`;
      });

    if (after !== before) {
      root.innerHTML = after;
    }
  }

  function applyLayoutClass(list, layout, kind) {
    const l = String(layout || "").trim().toLowerCase();
    const columns =
      l === "grid3" ? 3 :
      l === "grid4" ? 4 :
      l === "row" ? 1 :
      2;

    list.classList.add("lfdl-embed-grid", "grid", `grid--${columns}-col-desktop`, "grid--1-col-tablet-down");

    if (kind === "collection") {
      list.classList.add("collection-list", "contains-card", "contains-card--collection");
    } else {
      list.classList.add("product-grid", "contains-card", "contains-card--product", "lfdl-featured-collection");
    }
  }

  function buildSingleWrapper(kind, align) {
    const wrapper = document.createElement("div");
    wrapper.className = "lfdl-embed-single";

    const alignValue = String(align || "").trim().toLowerCase();
    if (alignValue === "center") wrapper.classList.add("is-center");
    if (alignValue === "wide") wrapper.classList.add("is-wide");
    if (alignValue === "left") wrapper.classList.add("is-left");
    if (alignValue === "right") wrapper.classList.add("is-right");

    const list = document.createElement("ul");
    list.setAttribute("role", "list");
    applyLayoutClass(list, "row", kind);

    wrapper.appendChild(list);
    return { wrapper, list };
  }

  async function renderSingle(node, kind) {
    const handleAttr = kind === "collection" ? "data-collection-handle" : "data-product-handle";
    const handle = normalizeHandle(node.getAttribute(handleAttr));
    if (!handle) return;

    const align = node.getAttribute("data-align") || "";
    const { wrapper, list } = buildSingleWrapper(kind, align);

    node.classList.add("is-loading");

    try {
      const url =
        kind === "collection"
          ? `/collections/${handle}?view=lfdl-card`
          : `/products/${handle}?view=lfdl-card`;
      const html = await fetchCardHtml(url);
      const card = createElementFromHtml(html);
      if (!card) {
        node.replaceWith(buildError(`${kind === "collection" ? "Collection" : "Produit"} introuvable: ${handle}`));
        return;
      }

      const item = document.createElement("li");
      item.className = "grid__item";
      item.appendChild(card);
      list.appendChild(item);
      node.replaceWith(wrapper);
    } catch (e) {
      node.replaceWith(buildError(`${kind === "collection" ? "Collection" : "Produit"} introuvable: ${handle}`));
    }
  }

  async function renderMultiple(node, kind) {
    const handlesAttr = kind === "collection" ? "data-collection-handles" : "data-product-handles";
    const handles = parseHandles(node.getAttribute(handlesAttr));
    if (!handles.length) return;

    const layout = node.getAttribute("data-layout") || "grid2";
    const list = document.createElement("ul");
    list.setAttribute("role", "list");
    applyLayoutClass(list, layout, kind);

    node.classList.add("is-loading");

    const label = kind === "collection" ? "Collection" : "Produit";
    const urls = handles.map((handle) =>
      kind === "collection"
        ? `/collections/${handle}?view=lfdl-card`
        : `/products/${handle}?view=lfdl-card`
    );

    const results = await Promise.all(
      urls.map((url) => fetchCardHtml(url).catch(() => null))
    );

    let hasAny = false;
    results.forEach((html, index) => {
      const handle = handles[index];
      const item = document.createElement("li");
      item.className = "grid__item";
      if (html) {
        const card = createElementFromHtml(html);
        if (card) {
          item.appendChild(card);
          hasAny = true;
        } else {
          item.appendChild(buildError(`${label} introuvable: ${handle}`));
        }
      } else {
        item.appendChild(buildError(`${label} introuvable: ${handle}`));
      }
      list.appendChild(item);
    });

    if (!hasAny) {
      node.replaceWith(buildError(`Erreur de chargement des ${kind === "collection" ? "collections" : "produits"}`));
      return;
    }

    node.replaceWith(list);
  }

  async function renderSingles() {
    const productNodes = Array.from(document.querySelectorAll(".lfdl-embed-product[data-product-handle]"));
    for (const node of productNodes) {
      await renderSingle(node, "product");
    }

    const collectionNodes = Array.from(document.querySelectorAll(".lfdl-embed-collection[data-collection-handle]"));
    for (const node of collectionNodes) {
      await renderSingle(node, "collection");
    }
  }

  async function renderMultiples() {
    const productNodes = Array.from(document.querySelectorAll(".lfdl-embed-products[data-product-handles]"));
    for (const node of productNodes) {
      await renderMultiple(node, "product");
    }

    const collectionNodes = Array.from(document.querySelectorAll(".lfdl-embed-collections[data-collection-handles]"));
    for (const node of collectionNodes) {
      await renderMultiple(node, "collection");
    }
  }

  function init() {
    const roots = Array.from(
      document.querySelectorAll(".lfdl-article-layout__content, .article-template__content, .rte")
    );
    if (!roots.length) return;

    roots.forEach((root) => replaceTokensInHtml(root));

    renderSingles();
    renderMultiples();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
